import type { StudentWorksheet } from '~/@entities/StudentWorksheet'
import type {
  Blank,
  CreateReportCommentData,
  CreateReportData,
  EssayReportCommentCreateRequest,
  EssayReportCreateRequest,
  EssayScoringOpenRequest,
  GetEssayScoringsData,
  GetEssayScoringSummariesByGradeData,
  GetEssayScoringSummariesByGradeParams,
  GetEssayScoringSummariesByLessonClassData,
  GetEssayScoringSummariesByLessonClassParams,
  GetGroupReportData,
  GetGroupReportParams,
  GroupEssayReportCreateRequest,
  Score,
  UpdateEssayScoringData,
} from '~/api-spec/__generated__/main'
import { httpClient } from '~/http/client'

import {
  adaptCreatedEssayReport,
  adaptEssayReportComment,
  adaptEssayReportFile,
  adaptEssayScoringDetail,
  adaptEssayScoringSaveResult,
  adaptEssayScoringStudents,
} from './adapters'
import type {
  CreatedEssayReport,
  EssayReportComment,
  EssayReportFile,
  EssayScoringDetail,
  EssayScoringSaveResult,
  EssayScoringStudent,
} from './schemas'

export type EssayScoringStudentsByGradeParams = GetEssayScoringSummariesByGradeParams
export type EssayScoringStudentsByLessonClassParams = GetEssayScoringSummariesByLessonClassParams

// 저장 바디는 essayType 으로 갈린다. 빈칸형은 판정한 슬롯만 싣고 점수는 서버가 낸다
export type EssayBlankScoringSave = Blank & { essayType: 'BLANK' }
// 점수 없이 첨삭만 저장할 때 score 를 null 로 싣는다(0점과 구분). 그런데 서버의 Score.score 는 non-null Int 라
// jackson-module-kotlin 이 null 을 400 으로 거부할 것으로 보인다. 2026-10-07 백엔드 origin/develop(85d9b076a)·
// origin/main(af70bb052) 의 essayScoring/controller/request/EssayScoringUpdateRequest.kt 로 확인(라이브 미확인).
// 서버가 nullable 로 열면 score 줄을 지운다.
export type EssayScoreScoringSave = Omit<Score, 'essayType' | 'score' | 'feedback'> & {
  essayType: Exclude<Score['essayType'], 'BLANK'>
  score: Score['score'] | null
  // 키를 빼면 부분 갱신이 되어 지운 첨삭이 남으므로 항상 싣는다
  feedback: NonNullable<Score['feedback']>
}
export type EssayScoringSave = EssayBlankScoringSave | EssayScoreScoringSave

export type EssayScoringOpenUpdate = EssayScoringOpenRequest
export type EssayReportCommentCreation = EssayReportCommentCreateRequest

// 서버는 shareToCreverse 를 기본값 false 로 받지만(Kotlin `Boolean = false`) 스펙은 필수로 그린다.
// 공유 권한이 없으면 싣지 않는다. 2026-10-07 백엔드 origin/develop(85d9b076a) 의
// essayScoring/controller/request/EssayReportRequest.kt 로 확인.
type _WithOptionalShare<T extends { shareToCreverse: boolean }> = Omit<T, 'shareToCreverse'> &
  Partial<Pick<T, 'shareToCreverse'>>
export type EssayReportCreation = _WithOptionalShare<EssayReportCreateRequest>
export type EssayGroupReportCreation = _WithOptionalShare<GroupEssayReportCreateRequest>

/***************** 채점 *****************/
export const getEssayScoringStudentsByGrade = async ({
  schoolType,
  grade,
  worksheetId,
}: EssayScoringStudentsByGradeParams): Promise<EssayScoringStudent[]> => {
  const body = await httpClient().get<GetEssayScoringSummariesByGradeData>(
    `/student-worksheet/grade/${schoolType}/${grade}/${worksheetId}/essay-scoring`,
  )
  return adaptEssayScoringStudents(body)
}

export const getEssayScoringStudentsByLessonClass = async ({
  lessonClassId,
  worksheetId,
}: EssayScoringStudentsByLessonClassParams): Promise<EssayScoringStudent[]> => {
  const body = await httpClient().get<GetEssayScoringSummariesByLessonClassData>(
    `/student-worksheet/lesson-class/${lessonClassId}/${worksheetId}/essay-scoring`,
  )
  return adaptEssayScoringStudents(body)
}

export const getEssayScoringDetail = async (
  studentWorksheetId: StudentWorksheet['id'],
): Promise<EssayScoringDetail> => {
  const body = await httpClient().get<GetEssayScoringsData>(
    `/student-worksheet/assign/${studentWorksheetId}/essay-scoring`,
  )
  return adaptEssayScoringDetail(body)
}

// 멱등 upsert. AI 채점 전 학습지에 처음 저장하면 수동 채점으로 바뀌고 되돌릴 수 없다. 채점 중(IN_PROGRESS) 문항은 409
export const saveEssayScoring = async (
  studentWorksheetId: StudentWorksheet['id'],
  payload: EssayScoringSave,
): Promise<EssayScoringSaveResult> => {
  const body = await httpClient().patch<UpdateEssayScoringData>(
    `/student-worksheet/assign/${studentWorksheetId}/essay-scoring`,
    payload,
  )
  return adaptEssayScoringSaveResult(body)
}

// 전 문항이 채점되지 않은 학습지가 섞이면 400
export const updateEssayScoringOpen = async (payload: EssayScoringOpenUpdate): Promise<void> => {
  await httpClient().patch('/student-worksheet/essay-scoring-open', payload)
}

/***************** 보고서 *****************/
export const createEssayReportComment = async (
  payload: EssayReportCommentCreation,
): Promise<EssayReportComment> => {
  const body = await httpClient().post<CreateReportCommentData>('/report/essay/comment', payload)
  return adaptEssayReportComment(body)
}

// 렌더가 끝난 뒤 응답하므로 받은 URL 로 바로 내려받는다
export const createEssayReport = async (
  payload: EssayReportCreation,
): Promise<CreatedEssayReport> => {
  const body = await httpClient().post<CreateReportData>('/report/essay', payload)
  return adaptCreatedEssayReport(body)
}

// 먼저 응답하고 뒤에서 만든다. 끝나면 알림의 '다운로드' 로 알린다
export const createEssayGroupReport = async (payload: EssayGroupReportCreation): Promise<void> => {
  await httpClient().post('/report/essay/group', payload)
}

// 알림 payload 의 id 로 zip 다운로드 URL 을 발급받는다(90일 보관)
export const getEssayGroupReportFile = async (
  params: GetGroupReportParams,
): Promise<EssayReportFile> => {
  const body = await httpClient().get<GetGroupReportData>('/report/essay/group/download', {
    params,
  })
  return adaptEssayReportFile(body)
}
